import { useTheme } from '@/context/ThemeContext';

const phases = [
  {
    number: '01',
    title: 'Identify',
    icon: 'ri-search-eye-line',
    description:
      'We map every breakdown point across your intake, conversion, and fulfillment processes. No assumption is made — every friction point is documented and prioritized.',
    detail: 'Discovery call, process audit, gap analysis',
  },
  {
    number: '02',
    title: 'Design',
    icon: 'ri-draft-line',
    description:
      'We architect a tailored system that aligns precisely with your operational flow. Every component is designed with your team, your clients, and your growth trajectory in mind.',
    detail: 'System architecture, workflow mapping, approval',
  },
  {
    number: '03',
    title: 'Deploy',
    icon: 'ri-rocket-line',
    description:
      'We integrate seamlessly into your existing operations without disruption. Your team continues working while the system activates in the background.',
    detail: 'Integration, testing, go-live, team onboarding',
  },
  {
    number: '04',
    title: 'Optimize',
    icon: 'ri-bar-chart-grouped-line',
    description:
      'Continuous monitoring and refinement ensure peak performance. As your business evolves, your systems adapt — maintaining consistency regardless of volume or complexity.',
    detail: 'Performance review, iteration, scaling',
  },
];

export default function HowItWorks() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="how-it-works" className={`py-28 px-6 md:px-10 transition-colors duration-500 ${
      isDark ? 'bg-[#0d2847]' : 'bg-[#f8f9fb]'
    }`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <span className={`text-xs font-medium tracking-widest uppercase mb-4 block ${
            isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
          }`}>
            THE PROCESS
          </span>
          <h2 className={`text-4xl md:text-5xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-[#0b1f3a]'
          }`}>
            Structured Execution in Four Phases
          </h2>
          <p className={`text-base max-w-xl mx-auto leading-relaxed ${
            isDark ? 'text-white/50' : 'text-slate-500'
          }`}>
            Every engagement follows a proven methodology designed to eliminate guesswork and deliver measurable results from day one.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting line */}
          <div className={`hidden md:block absolute top-16 left-0 right-0 h-px bg-gradient-to-r from-transparent ${
            isDark ? 'via-[#14b8c8]/30' : 'via-[#0d7a8a]/30'
          } to-transparent`}></div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {phases.map((phase, i) => (
              <div key={phase.number} className="relative flex flex-col">
                {/* Number + Icon */}
                <div className="flex flex-col items-start mb-6">
                  <span className={`text-7xl font-thin leading-none mb-2 select-none ${
                    isDark ? 'text-[#14b8c8]/15' : 'text-[#0d7a8a]/15'
                  }`}>
                    {phase.number}
                  </span>
                  <div className={`w-12 h-12 flex items-center justify-center border rounded-xl ${
                    isDark
                      ? 'bg-[#14b8c8]/10 border-[#14b8c8]/20'
                      : 'bg-[#0d7a8a]/10 border-[#0d7a8a]/20'
                  }`}>
                    <i className={`${phase.icon} text-xl ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}></i>
                  </div>
                </div>

                {/* Content */}
                <div className={`flex-1 rounded-xl p-6 border transition-colors duration-300 ${
                  isDark
                    ? 'bg-[#0b1f3a] border-white/10 hover:border-[#14b8c8]/30'
                    : 'bg-white border-slate-200 hover:border-[#0d7a8a]/30'
                }`}>
                  <h4 className={`font-bold text-xl mb-3 ${isDark ? 'text-white' : 'text-[#0b1f3a]'}`}>{phase.title}</h4>
                  <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{phase.description}</p>
                  <div className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                    <span className={`text-xs font-medium ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}>{phase.detail}</span>
                  </div>
                </div>

                {/* Arrow connector (mobile) */}
                {i < phases.length - 1 && (
                  <div className="md:hidden flex justify-center my-4">
                    <i className={`text-xl ${isDark ? 'text-[#14b8c8]/40' : 'text-[#0d7a8a]/40'}`}>
                      <i className="ri-arrow-down-line"></i>
                    </i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className={`text-sm mb-6 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
            Ready to begin? The first step is a conversation.
          </p>
          <button
            onClick={() => {
              const el = document.querySelector('#contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-base transition-colors duration-200 cursor-pointer whitespace-nowrap ${
              isDark
                ? 'bg-[#14b8c8] text-[#0b1f3a] hover:bg-[#12a8b8]'
                : 'bg-[#0d7a8a] text-white hover:bg-[#0a6878]'
            }`}
          >
            Start the Process
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}